import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { instanceHeaders } from "@/hooks/use-access";
import { BINDING_QUERY_KEY, DESTINATION_READINESS_PATH } from "@/hooks/use-binding";
import { OVERVIEW_PATH } from "@/hooks/use-overview";
import { CREDENTIALS_PATH, apiRequest } from "@/lib/api";
import { instanceKey } from "@/lib/instanceKey";
import type { DestinationReadinessView } from "@shared/types/instances";
import type { DestinationTestBody, DestinationTestView } from "@shared/types/instancesAdmin";
import {
  DATABRICKS_COMING_SOON_MESSAGE,
  DESTINATION_DRAFT_BANNER,
  DESTINATION_NOT_SET_MESSAGE,
  readinessFieldName,
} from "@shared/warehouse/destinationReadiness";

/**
 * #923, #375: whether this instance's destination may take a run, as the banner and the run
 * buttons read it: the unaudited `GET /instances/current/destination-readiness` (every role,
 * `instance.binding.read`; "not_set" when there is no binding), because the binding GET is an
 * audited read and must not be asked on every screen. A binding save writes the returned view's
 * readiness into this key (`useSaveBinding`), so the banner moves at once; a test invalidates it.
 */

/** Test and Check now (#923): a POST, PlatformAdmin only, audited `binding.test`. */
export const DESTINATION_TEST_PATH = "/api/v2/instances/current/binding/test";

/** The banner's read is cheap but not free: once every five minutes, and on every save or test. */
const READINESS_STALE_MS = 5 * 60_000;

/** The readiness, or null while unknown (loading, failed, or a server without the route). */
export function useDestinationReadiness(enabled = true): DestinationReadinessView | null {
  const { selectedInstance } = useTenantInstance();
  const query = useQuery<DestinationReadinessView>({
    queryKey: instanceKey(DESTINATION_READINESS_PATH, selectedInstance?.id),
    enabled: enabled && Boolean(selectedInstance),
    staleTime: READINESS_STALE_MS,
    queryFn: async ({ signal }) =>
      apiRequest<DestinationReadinessView>(DESTINATION_READINESS_PATH, {
        signal,
        headers: instanceHeaders(selectedInstance?.id),
      }),
  });
  return query.data ?? null;
}

/** Owner 2026-10-13 (2B): a Databricks destination is never Ready; it reads "Coming soon". */
export function isComingSoon(readiness: DestinationReadinessView): boolean {
  return readiness.state === "draft" && readiness.reason === "databricks_coming_soon";
}

export interface ChipPresentation {
  label: string;
  appearance: "tint" | "outline";
  hint: string;
}

/**
 * The chip, by state (the Destination page and, #2518, the Audit trail drawer). Untested is never shown as working (owner 1A): a Ready destination carried
 * over from before testing existed reads "Ready (not tested yet)" and its hint claims no pass.
 * A Databricks destination is never Ready (owner 2B) and reads "Coming soon".
 */
export function chipOf(readiness: DestinationReadinessView): ChipPresentation {
  if (readiness.state === "not_set") {
    return {
      label: "Not set",
      appearance: "outline",
      hint: "No destination yet. Imports and transformations won't run until one is added.",
    };
  }
  if (isComingSoon(readiness)) {
    return {
      label: "Coming soon",
      appearance: "outline",
      hint: DATABRICKS_COMING_SOON_MESSAGE,
    };
  }
  if (readiness.state === "draft") {
    return {
      label: "Draft",
      appearance: "outline",
      hint: "Imports and transformations won't run until the destination passes its test.",
    };
  }
  if (readiness.source === "carried") {
    return {
      label: "Ready (not tested yet)",
      appearance: "outline",
      hint: "Imports ran with these settings before testing existed. They have not been tested here.",
    };
  }
  return {
    label: "Ready",
    appearance: "tint",
    hint: "The destination passed its test. Imports and transformations run.",
  };
}

/** Why nothing runs, in one sentence (a run button's tooltip), or null when it may run. */
export function destinationBlockReason(readiness: DestinationReadinessView | null): string | null {
  if (!readiness || readiness.state === "ready") return null;
  if (readiness.state === "not_set") return DESTINATION_NOT_SET_MESSAGE;
  if (isComingSoon(readiness)) return DATABRICKS_COMING_SOON_MESSAGE;
  return DESTINATION_DRAFT_BANNER;
}

/** The second line of a Draft banner: what is missing, or why it is Draft. */
export function readinessReasonLine(readiness: DestinationReadinessView): string | null {
  if (readiness.state !== "draft") return null;
  switch (readiness.reason) {
    case "incomplete":
      return readiness.missing.length > 0
        ? `Missing: ${readiness.missing.map(readinessFieldName).join(", ")}.`
        : "Some fields are missing.";
    case "never_tested":
      return "It has not been tested yet.";
    case "test_failed":
      return "Its last test failed.";
    case "changed":
      return "It changed since its last test.";
    case "databricks_coming_soon":
      return DATABRICKS_COMING_SOON_MESSAGE;
    default:
      return null;
  }
}

/** Runs a Test or a Check now; the binding and the credential checks are read again after it. */
export function useDestinationTest() {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  return useMutation({
    mutationFn: async (body: DestinationTestBody) =>
      apiRequest<DestinationTestView>(DESTINATION_TEST_PATH, {
        method: "POST",
        body,
        headers: instanceHeaders(selectedInstance?.id),
      }),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: instanceKey(BINDING_QUERY_KEY, selectedInstance?.id),
        }),
        client.invalidateQueries({
          queryKey: instanceKey(DESTINATION_READINESS_PATH, selectedInstance?.id),
        }),
        client.invalidateQueries({ queryKey: [CREDENTIALS_PATH] }),
        client.invalidateQueries({ queryKey: [OVERVIEW_PATH] }),
      ]);
    },
  });
}
